import { AdPlacement } from '@openx/types/adUnit.ts';
import { DistributionChannel } from '@openx/types/distributionChannel.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { InventoryQualityValue } from './useIqScoringAutoCheck.ts';

type UseInventoryQualityPropsArgs = {
  isPub: boolean | undefined;
  adPlacement?: string | null;
  distributionChannel?: string | null;
  inventoryQuality?: InventoryQualityValue;
};

type InventoryQualityProps =
  | {
      disabled: boolean;
      error: boolean;
      helperText?: string;
    }
  | undefined;

export const useInventoryQualityProps = ({
  isPub,
  adPlacement,
  distributionChannel,
  inventoryQuality,
}: UseInventoryQualityPropsArgs): InventoryQualityProps => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (isPub) {
      return undefined;
    }

    const lowerCaseAdPlacement = (adPlacement ?? '').toLowerCase();
    const isBannerOrVideo =
      lowerCaseAdPlacement === AdPlacement.Banner.toLowerCase() ||
      lowerCaseAdPlacement === AdPlacement.Video.toLowerCase();

    const channels = (distributionChannel ?? '')
      .split(',')
      .map(v => v.trim().toLowerCase())
      .filter(Boolean);

    const isWeb = channels.includes(DistributionChannel.Web.toLowerCase());
    const isApp = channels.includes(DistributionChannel.App.toLowerCase());

    const envValid = isWeb && !isApp && isBannerOrVideo;
    const isChecked = !!inventoryQuality;

    const disabled = !envValid && !isChecked;
    const error = !envValid && isChecked;
    const helperText = error ? t('This option is available only for Web Banner or Web Video targeting.') : undefined;

    return {
      disabled,
      error,
      helperText,
    };
  }, [isPub, adPlacement, distributionChannel, inventoryQuality, t]);
};
